import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { radioStationService, RadioStationQueryParams } from '@/services/radioStationService';
import { CreateRadioStationRequest } from '@/types/model';

export const radioStationKeys = {
  all: ['radioStations'] as const,
  lists: () => [...radioStationKeys.all, 'list'] as const,
  list: (params: RadioStationQueryParams) => [...radioStationKeys.lists(), params] as const,
  details: () => [...radioStationKeys.all, 'detail'] as const,
  detail: (id: string) => [...radioStationKeys.details(), id] as const,
};

export const useRadioStations = (params: RadioStationQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: radioStationKeys.list(params),
    queryFn: () => radioStationService.getRadioStations(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useRadioStation = (id: string) => {
  return useQuery({
    queryKey: radioStationKeys.detail(id),
    queryFn: () => radioStationService.getRadioStationById(id),
    enabled: !!id,
  });
};

export const useCreateRadioStation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (radioStationData: CreateRadioStationRequest) =>
      radioStationService.createRadioStation(radioStationData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: radioStationKeys.lists() });
    },
  });
};

export const useUpdateRadioStation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateRadioStationRequest> }) =>
      radioStationService.updateRadioStation(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: radioStationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: radioStationKeys.lists() });
    },
  });
};

export const useDeleteRadioStation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => radioStationService.deleteRadioStation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: radioStationKeys.lists() });
    },
  });
};

export const useToggleRadioStationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      radioStationService.toggleRadioStationStatus(id, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: radioStationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: radioStationKeys.lists() });
    },
  });
};
export const useRadioStationsForDropdown = (enabled = true) => useQuery({
  queryKey: [...radioStationKeys.all, 'dropdown'],
  queryFn: () => radioStationService.getRadioStationsForDropdown(),
  enabled,
  staleTime: 5 * 60 * 1000,
});
